import { useCallback } from "react";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import ContextAttachment from "components/ContextAttachment";
import NotFoundPage from "components/error/NotFoundPage";
import { EmptystateLinkColored, EmptystateMagnetColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import Box from "ds/components/Box";
import EmptyState from "ds/components/EmptyState";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useAttachContext from "shared/Context/useAttachContext";
import useDetachContext from "shared/Context/useDetachContext";
import useGetAttachedContexts from "shared/Context/useGetAttachedContexts";
import PageInfoHeader from "ds/components/PageInfoHeader";
import PermissionGateButton from "components/PermissionGate/Button";
import CollapsibleSection from "ds/components/CollapsibleSection";
import Counter from "ds/components/Counter";

import { ModuleContext } from "../Context";
import { showAttachContextDrawer } from "./components/AttachContextDrawer";

const ModuleContexts = () => {
  const { module, canManageModule } = useTypedContext(ModuleContext);

  useTitle(`Contexts · ${module.name}`);

  const {
    manuallyAttachedContexts,
    autoAttachedContexts,
    manuallyAttachedContextIds,
    isPageLoading,
    isPageNotFound,
    error,
  } = useGetAttachedContexts({
    entityId: module.id,
    entityType: "module",
  });

  useBreadcrumbs([
    {
      title: "Modules",
      link: "/modules",
    },
    {
      title: module.id,
    },
  ]);

  const { attachContext } = useAttachContext(
    ["GetModuleAttachedContexts"],
    "Context priority successfully changed"
  );

  const { detachContext } = useDetachContext(["GetModuleAttachedContexts"]);

  const handleDetachContext = useCallback(
    (id: string) => {
      detachContext(id);
    },
    [detachContext]
  );

  const handleChangePriority = useCallback(
    (id: string, priority: number) => {
      attachContext(id, module.id, priority);
    },
    [attachContext, module.id]
  );

  const handleOpenAttachContextDrawer = () => {
    showAttachContextDrawer({
      attachedContextIds: manuallyAttachedContextIds,
      stackId: module.id,
      spaceId: module.spaceDetails.id,
    });
  };

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (isPageLoading) {
    return <PageLoading />;
  }

  if (isPageNotFound) {
    return <NotFoundPage />;
  }

  return (
    <>
      <PageInfoHeader title="Contexts">
        <PermissionGateButton
          access={canManageModule}
          variant="primary"
          onPress={handleOpenAttachContextDrawer}
        >
          Attach context
        </PermissionGateButton>
      </PageInfoHeader>
      <Box gap="x-large" direction="column">
        <CollapsibleSection
          ariaLevel={3}
          title="Attached directly"
          contentGap="medium"
          ariaLabel="Attached directly"
          titleSuffix={<Counter count={manuallyAttachedContexts.length} />}
          initialIsCollapsed={false}
        >
          {manuallyAttachedContexts.map((context) => (
            <ContextAttachment
              key={context.id}
              context={context}
              onPriorityChange={handleChangePriority}
              onDetach={handleDetachContext}
              canUpdate={canManageModule}
              showEditLink
            />
          ))}

          {manuallyAttachedContexts.length === 0 && (
            <EmptyState
              padding="0 0 medium"
              icon={EmptystateLinkColored}
              title="No contexts attached yet"
              caption={
                <>
                  This module does not have any contexts attached yet.
                  <br />
                  Use attach context button to attach contexts to the module.
                </>
              }
            />
          )}
        </CollapsibleSection>

        <CollapsibleSection
          ariaLevel={3}
          title="Auto-attached"
          contentGap="medium"
          titleSuffix={<Counter count={autoAttachedContexts.length} />}
          ariaLabel="Auto-attached"
          initialIsCollapsed={false}
        >
          {autoAttachedContexts.map((context) => (
            <ContextAttachment
              key={context.id}
              context={context}
              stackLabels={module.labels}
              showEditLink
            />
          ))}

          {autoAttachedContexts.length === 0 && (
            <EmptyState
              padding="0 0 medium"
              icon={EmptystateMagnetColored}
              title="No contexts auto-attached yet"
              caption={
                <>
                  This is where auto-attached contexts will appear.
                  <br />
                  Use autoattach label to attach context to many modules at the same time.
                </>
              }
            />
          )}
        </CollapsibleSection>
      </Box>
    </>
  );
};

export default ModuleContexts;
